/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Initializes and sets up the horizontal scroll functionality,
 * scroll button visibility, and auto-scrolling.
 */
function setupHorizontalScroll() {
    // === DOM Element Selection ===
    const listScrollContainer = document.querySelector(".js-list-scroll");
    const imageRowHscrollItem = document.querySelector(".js-image-row-hscroll-item");
    const clickToHscrollButtons = document.querySelectorAll(".js-click-to-hscroll");
    const scrollRightButton = document.querySelector(".js-click-to-hscroll.scroll-right");
    const scrollLeftButton = document.querySelector(".js-click-to-hscroll.scroll-left");
    const pressLogos = listScrollContainer ? listScrollContainer.querySelectorAll(".press-logo") : [];

    if (!listScrollContainer) return;

    // === Helper Functions ===

    /**
     * Updates the visibility of the scroll buttons based on the current scroll position.
     * @param {number} currentScrollLeft The current horizontal scroll position.
     */
    function updateScrollButtonVisibility(currentScrollLeft) {
        const containerWidth = listScrollContainer.offsetWidth;
        const scrollWidth = listScrollContainer.scrollWidth;
        const isScrolledToStart = currentScrollLeft <= 10;
        const isScrolledToEnd = scrollWidth - currentScrollLeft - containerWidth <= 10;

        // Left button (Scroll-Left)
        if (scrollLeftButton) {
            if (isScrolledToStart) {
                scrollLeftButton.classList.remove("visible");
            } else {
                scrollLeftButton.classList.add("visible");
            }
        }

        // Right button (Scroll-Right)
        if (scrollRightButton) {
            if (isScrolledToEnd) {
                scrollRightButton.classList.remove("visible");
            } else {
                scrollRightButton.classList.add("visible");
            }
        }
    }

    // === Initial Setup ===

    // 1. Calculate total content width
    let totalContentWidth = 0;
    pressLogos.forEach(logo => {
        const style = window.getComputedStyle(logo);
        const marginLeft = parseFloat(style.marginLeft);
        const marginRight = parseFloat(style.marginRight);
        totalContentWidth += logo.offsetWidth + marginLeft + marginRight;
    });

    // 2. Determine if there is overflow
    if (totalContentWidth > listScrollContainer.offsetWidth) {
        listScrollContainer.classList.add("overflow-content");
    } else {
        listScrollContainer.classList.remove("overflow-content");
    }

    // 3. Add 'last-item' class to the last logo
    if (pressLogos.length > 0) {
        pressLogos[pressLogos.length - 1].classList.add("last-item");
    }

    // 4. Initialize right button visibility
    if (scrollRightButton) {
        scrollRightButton.classList.add("visible");
    }

    // 5. Update initial button visibility
    updateScrollButtonVisibility(listScrollContainer.scrollLeft);


    // === Event Listeners ===

    // 1. Scroll Event: Update button visibility when user scrolls
    listScrollContainer.addEventListener("scroll", () => {
        updateScrollButtonVisibility(listScrollContainer.scrollLeft);
    });

    // 2. Click Button Event: Programmatically scroll
    clickToHscrollButtons.forEach(button => {
        button.addEventListener("click", function () {
            const direction = this.dataset.direction;
            const scrollDistance = imageRowHscrollItem ? imageRowHscrollItem.offsetWidth : 0;
            const currentScrollLeft = listScrollContainer.scrollLeft;
            let targetScrollLeft = currentScrollLeft;
            const scrollStep = scrollDistance + (direction === 'right' ? 30 : -15);

            if (direction === "right") {
                targetScrollLeft = currentScrollLeft + scrollStep;
                // Limit scrolling to the right end
                if (targetScrollLeft > listScrollContainer.scrollWidth - listScrollContainer.offsetWidth) {
                    targetScrollLeft = listScrollContainer.scrollWidth - listScrollContainer.offsetWidth;
                }
            } else if (direction === "left") {
                targetScrollLeft = currentScrollLeft - scrollDistance - 15;
                // Limit scrolling to the left end
                if (targetScrollLeft < 0) {
                    targetScrollLeft = 0;
                }
            }

            // Smooth scroll animation
            listScrollContainer.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
            });

            // Update visibility after the animation finishes
            setTimeout(() => {
                updateScrollButtonVisibility(listScrollContainer.scrollLeft);
            }, 850);
        });
    });

    // 3. Autoplay: Click scroll-right button every 3 seconds
    setInterval(() => {
        if (scrollRightButton && scrollRightButton.classList.contains('visible')) {
            // Simulate click on the scroll-right button
            scrollRightButton.click();
        } else if (scrollRightButton) {
            // If already at the end and the button is hidden, scroll back to the start for 'looping'.
            listScrollContainer.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
            // Update visibility after scrolling back
            setTimeout(() => {
                updateScrollButtonVisibility(listScrollContainer.scrollLeft);
            }, 850);
        }
    }, 3000);

    // 4. Resize Event: Update overflow class and button visibility on resize
    window.addEventListener("resize", () => {
        let newTotalContentWidth = 0;
        pressLogos.forEach(logo => {
            const style = window.getComputedStyle(logo);
            const marginLeft = parseFloat(style.marginLeft);
            const marginRight = parseFloat(style.marginRight);
            newTotalContentWidth += logo.offsetWidth + marginLeft + marginRight;
        });

        if (newTotalContentWidth > listScrollContainer.offsetWidth) {
            listScrollContainer.classList.add("overflow-content");
        } else {
            listScrollContainer.classList.remove("overflow-content");
        }
        updateScrollButtonVisibility(listScrollContainer.scrollLeft);
    });
}

// === Call Function When DOM is Ready ===
document.addEventListener("DOMContentLoaded", setupHorizontalScroll);

/**
 * Desktop PDP gallery is a custom masonry grid, not <product-media>, so Focal's
 * variant:change handler never moves the featured image. Promote the selected
 * variant's image immediately while section HTML is still fetching.
 */
function syncDesktopGalleryToVariant(variant) {
  const mediaId = variant && variant.featured_media && variant.featured_media.id;
  if (!mediaId) return;

  const gallery = document.querySelector(".product-media-gallery__list.hide-desktop-and-down");
  if (!gallery) return;

  const item = gallery.querySelector(`[data-media-id$="-${mediaId}"]`);
  if (!item) return;

  const quote = gallery.querySelector(".product-media-gallery__item--quote");
  if (quote) {
    if (item.previousElementSibling !== quote) quote.after(item);
  } else if (gallery.firstElementChild !== item) {
    gallery.prepend(item);
  }
}

document.addEventListener("variant:change", (event) => {
  syncDesktopGalleryToVariant(event.detail && event.detail.variant);
});

/**
 * HOEY collaboration.
 * The discount applies from umamipapi.com.au/HOEY, or when HOEY13 is typed in.
 * The question sits above the cart discount field for those visits only.
 * The answer is stored on the order as an additional detail.
 */
(function () {
  var ATTRIBUTE = "How did you hear about this?";
  var CODE = "HOEY13";
  var LINK_KEY = "umamiHoeySession";
  var TYPED_KEY = "umamiHoeyTyped";
  var ANSWERS = ["Instagram", "TikTok", "YouTube", "Facebook", "A friend", "Other"];
  var answered = "";
  var clearing = false;

  function allowDiscount() {
    try {
      return sessionStorage.getItem(LINK_KEY) === "1" || sessionStorage.getItem(TYPED_KEY) === "1";
    } catch (error) {
      return false;
    }
  }

  function noteArrival() {
    try {
      localStorage.removeItem("umamiHoeyLink");
    } catch (error) {}

    try {
      var params = new URLSearchParams(window.location.search);
      var source = (params.get("utm_source") || "").toUpperCase();
      var campaign = (params.get("utm_campaign") || "").toUpperCase();
      if (source === "HOEY" || campaign === CODE) sessionStorage.setItem(LINK_KEY, "1");
    } catch (error) {}
  }

  function cartHasCode(cart) {
    var codes = (cart && cart.discount_codes) || [];
    var index;
    for (index = 0; index < codes.length; index++) {
      if (String(codes[index].code || "").toUpperCase() === CODE) return true;
    }
    return false;
  }

  function readCart() {
    return fetch("/cart.js", { credentials: "same-origin" }).then(function (response) {
      return response.json();
    });
  }

  function clearDiscountCookie() {
    var host = window.location.hostname;
    var domains = ["", host, "." + host];
    var index;
    for (index = 0; index < domains.length; index++) {
      document.cookie = "discount_code=; Max-Age=0; path=/" + (domains[index] ? "; domain=" + domains[index] : "");
    }
  }

  function removeCode(cart) {
    var remaining = (cart.discount_codes || []).map(function (entry) {
      return entry.code;
    }).filter(function (code) {
      return String(code || "").toUpperCase() !== CODE;
    });

    clearDiscountCookie();
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ discount: remaining.join(",") })
    }).then(function (response) {
      return response.json();
    }).then(function (updated) {
      if (typeof window.upcartRefreshCart === "function") window.upcartRefreshCart();
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { cart: updated } }));
      return updated;
    });
  }

  function eachField(selector, callback) {
    document.querySelectorAll(selector).forEach(callback);
    var host = document.querySelector("#upCart");
    var root = host && host.shadowRoot;
    if (root) root.querySelectorAll(selector).forEach(callback);
  }

  function saveAnswer(value) {
    if (!value) return;
    readCart().then(function (cart) {
      var attributes = Object.assign({}, cart.attributes);
      attributes[ATTRIBUTE] = value;
      return fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attributes })
      });
    }).then(function (response) {
      if (!response.ok) throw new Error("Could not save");
      answered = value;
      eachField(".hoey-hear-field__note", function (note) {
        note.textContent = "Saved";
      });
    }).catch(function () {
      eachField(".hoey-hear-field__note", function (note) {
        note.textContent = "Please try again.";
      });
    });
  }

  function buildField() {
    var field = document.createElement("div");
    field.className = "hoey-hear-field";
    field.innerHTML =
      '<label class="hoey-hear-field__label">How did you hear about this?</label>' +
      '<select class="hoey-hear-field__select">' +
        '<option value="">Select one</option>' +
        ANSWERS.map(function (answer) {
          return '<option value="' + answer + '">' + answer + "</option>";
        }).join("") +
      "</select>" +
      '<p class="hoey-hear-field__note"></p>';
    var select = field.querySelector("select");
    select.value = answered;
    select.addEventListener("change", function () {
      eachField(".hoey-hear-field__select", function (other) {
        other.value = select.value;
      });
      saveAnswer(select.value);
    });
    return field;
  }

  function placeBefore(anchor) {
    if (!anchor || !anchor.parentNode) return;
    var parent = anchor.parentNode;
    var field = null;
    var child = parent.firstElementChild;
    while (child) {
      if (child.classList && child.classList.contains("hoey-hear-field") && child.nextElementSibling === anchor) {
        field = child;
        break;
      }
      child = child.nextElementSibling;
    }

    if (!allowDiscount()) {
      child = parent.firstElementChild;
      while (child) {
        var next = child.nextElementSibling;
        if (child.classList && child.classList.contains("hoey-hear-field") && next === anchor) child.remove();
        child = next;
      }
      return;
    }

    if (!field) parent.insertBefore(buildField(), anchor);
    else field.querySelector("select").value = answered;
  }

  function shadowStyle(root) {
    if (!root || root.querySelector("style[data-hoey-hear]")) return;
    var style = document.createElement("style");
    style.setAttribute("data-hoey-hear", "true");
    style.textContent = [
      ".hoey-hear-field { margin: 0 0 12px; padding: 12px 14px 14px; border: 1.5px solid #111; border-radius: 12px; background: #fff; }",
      ".hoey-hear-field__label { display: block; margin: 0 0 8px; color: #111; font-family: 'Barlow Condensed', sans-serif; font-size: 22px; font-weight: 600; line-height: 1; letter-spacing: 0.02em; text-transform: uppercase; }",
      ".hoey-hear-field__select { width: 100%; min-height: 44px; padding: 0 10px; border: 1px solid #111; border-radius: 8px; background: #fff; color: #111; font-family: 'Albert Sans', sans-serif; font-size: 15px; }",
      ".hoey-hear-field__note { margin: 8px 0 0; color: #111; font-family: 'Albert Sans', sans-serif; font-size: 13px; }",
      ".hoey-hear-field__note:empty { display: none; }"
    ].join("");
    root.appendChild(style);
  }

  function markTyped(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var box = target.closest(".upcart-discount-code-module, cart-discount-code, .cart-discount");
    if (!box) return;
    var applying = event.type === "submit" || target.closest("button, input[type='submit']");
    if (!applying) return;
    var input = box.querySelector("input");
    if (!input || input.value.trim().toUpperCase() !== CODE) return;
    try {
      sessionStorage.setItem(TYPED_KEY, "1");
    } catch (error) {}
    window.setTimeout(placeQuestion, 300);
  }

  function watchRoot(root) {
    if (!root || root.__hoeyWatch) return;
    root.__hoeyWatch = true;
    root.addEventListener("click", markTyped, true);
    root.addEventListener("submit", markTyped, true);
    if (root.nodeType === 11) {
      shadowStyle(root);
      new MutationObserver(function () { placeQuestion(); }).observe(root, { childList: true, subtree: true });
    }
  }

  function placeQuestion() {
    document.querySelectorAll("cart-discount-code, .cart-discount").forEach(function (box) {
      if (box.getClientRects().length) placeBefore(box);
    });

    var host = document.querySelector("#upCart");
    var root = host && host.shadowRoot;
    if (!root) return;
    watchRoot(root);
    var module = root.querySelector(".upcart-discount-code-module");
    if (!module) return;
    if (getComputedStyle(module).display === "none") {
      var leftover = module.previousElementSibling;
      if (leftover && leftover.classList.contains("hoey-hear-field")) leftover.remove();
      return;
    }
    placeBefore(module);
  }

  function refresh() {
    if (clearing) return;
    readCart().then(function (cart) {
      answered = (cart.attributes && cart.attributes[ATTRIBUTE]) || answered;
      if (!allowDiscount() && cartHasCode(cart)) {
        clearing = true;
        return removeCode(cart).then(function () {
          clearing = false;
          placeQuestion();
        });
      }
      placeQuestion();
    }).catch(function () {
      placeQuestion();
    });
  }

  noteArrival();
  watchRoot(document);
  document.addEventListener("DOMContentLoaded", refresh);
  document.documentElement.addEventListener("cart:updated", refresh);
  document.documentElement.addEventListener("cart:refresh", function () {
    if (!clearing) placeQuestion();
  });
  window.setInterval(placeQuestion, 1000);
})();
